import axios from 'axios';
import { bdmAppOrigin } from '../config/sfApiBase';

class OutlookService {
  constructor() {
    this.baseUrl = 'https://graph.microsoft.com/v1.0';
    this.proxyApiOrigin = bdmAppOrigin();
    this.clientId = process.env.REACT_APP_MSGRAPH_CLIENT_ID;
    this.clientSecret = process.env.REACT_APP_MSGRAPH_CLIENT_SECRET;
    this.tenantId = process.env.REACT_APP_MSGRAPH_TENANT_ID;

    // Flag to control test mode (defaults to false for production)
    this.isTestMode = false;
    this.testEmailAddress = 'damian@purelocations.com.au';

    // Send-mail uses server-side MSGRAPH_*; these are only for legacy /api/token calls from the browser.
    if (!this.clientId || !this.clientSecret || !this.tenantId) {
      console.warn(
        'REACT_APP_MSGRAPH_* not set in the SPA build. Email sending still works if the server has MSGRAPH_CLIENT_ID, MSGRAPH_CLIENT_SECRET, MSGRAPH_TENANT_ID.'
      );
    }
  }

  /**
   * Set test mode on or off
   * @param {boolean} enabled - Whether test mode should be enabled
   */
  setTestMode(enabled) {
    this.isTestMode = enabled;
    // console.log(`Email test mode is now ${enabled ? 'ENABLED' : 'DISABLED'}`);
    // console.log(`Emails will be sent to ${enabled ? this.testEmailAddress : 'actual recipients'}`);
    return this.isTestMode;
  }

  /**
   * Get current test mode status
   * @returns {boolean} Whether test mode is currently enabled
   */
  getTestMode() {
    return this.isTestMode;
  }

  /**
   * Get access token for Microsoft Graph API
   */
  async getAccessToken() {
    try {
      // console.log('Requesting access token from proxy server...');
      
      const tokenRequest = {
        client_id: this.clientId,
        client_secret: this.clientSecret,
        scope: 'https://graph.microsoft.com/.default',
        grant_type: 'client_credentials',
        tenant_id: this.tenantId
      };

      if (!this.clientId || !this.clientSecret || !this.tenantId) {
        throw new Error('Microsoft Graph credentials are not configured in the app (REACT_APP_MSGRAPH_*).');
      }

      const response = await axios.post(`${this.proxyApiOrigin}/api/token`, tokenRequest, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.data || !response.data.access_token) {
        console.error('Invalid token response:', response.data);
        throw new Error('Invalid token response from server');
      }

      // console.log('Successfully obtained access token');
      return response.data.access_token;
    } catch (error) {
      console.error('Error getting access token:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      
      const errorMessage = error.response?.data?.error_description || 
                          error.response?.data?.message ||
                          error.message;
      
      throw new Error('Failed to get access token: ' + errorMessage);
    }
  }

  /**
   * Send email via server proxy (Microsoft Graph token stays on the server).
   * @param {Object} emailData - The email data
   * @param {string} sendAsEmail - The email address to send as (owner mailbox)
   */
  async sendEmail(emailData, sendAsEmail) {
    try {
      console.log('Starting email send via server proxy...', {
        hasTo: !!emailData.to,
        hasSubject: !!emailData.subject,
        hasBody: !!emailData.body,
        sendAsEmail,
      });

      const payload = {
        to: emailData.to,
        subject: emailData.subject,
        body: emailData.body,
        sendAsEmail,
      };
      if (emailData.cc) payload.cc = emailData.cc;
      if (emailData.bcc) payload.bcc = emailData.bcc;

      const response = await axios.post(`${this.proxyApiOrigin}/api/msgraph/send-mail`, payload, {
        headers: { 'Content-Type': 'application/json' },
        validateStatus: () => true,
      });

      if (response.status !== 200 && response.status !== 202) {
        const d = response.data;
        const msg = d?.details || d?.error_description || d?.error || response.statusText;
        throw new Error(msg || `Send mail failed (${response.status})`);
      }

      console.log('Email send request accepted by server', {
        status: response.status,
        recipient: emailData.to,
        sendAsEmail,
      });

      return true;
    } catch (error) {
      // Enhanced error logging
      console.error('Detailed error in sendEmail:', {
        errorMessage: error.message,
        errorName: error.name,
        responseStatus: error.response?.status,
        responseStatusText: error.response?.statusText,
        responseData: error.response?.data,
        sendAsEmail,
        originalRecipient: emailData.to,
      });

      const graphErrorMessage = error.response?.data?.details || error.response?.data?.error_description;
      const graphErrorCode = error.response?.data?.error;
      const statusCode = error.response?.status;

      let errorMessage = 'Failed to send email: ';

      if (graphErrorMessage) {
        errorMessage += graphErrorCode ? `${graphErrorCode}: ${graphErrorMessage}` : graphErrorMessage;
      } else if (error.response?.data?.message) {
        errorMessage += error.response.data.message;
      } else if (statusCode) {
        errorMessage += `HTTP ${statusCode}: ${error.message}`;
      } else {
        errorMessage += error.message;
      }

      throw new Error(errorMessage);
    }
  }

  /**
   * Main method to compose and send email
   * @param {Object} emailData - The email data
   * @param {string} ownerEmail - The owner's email address to send as
   */
  async composeEmail(emailData, ownerEmail) {
    const mailbox = typeof ownerEmail === 'string' ? ownerEmail.trim() : '';
    if (!mailbox || !mailbox.includes('@')) {
      throw new Error(
        'A valid owner mailbox (email address) is required to send email. The contact may be missing Owner.Email in Salesforce.'
      );
    }
    try {
      // console.log('Composing email for owner:', ownerEmail);
      
      let emailToSend = emailData;
      
      // Only redirect and modify emails if in test mode
      if (this.isTestMode) {
        // console.log(`TEST MODE ACTIVE - Redirecting email to ${this.testEmailAddress}`);
        
        // Save original recipient for reference
        const originalRecipient = emailData.to;
        
        // Create a modified email object for testing
        emailToSend = {
          ...emailData,
          // Override the recipient with test email
          to: this.testEmailAddress,
          // Add original recipient information to the subject
          subject: `[TEST MODE - Original: ${originalRecipient}] ${emailData.subject}`,
          // Add a notice at the top of the email body
          body: `<div style="background-color: #f8d7da; color: #721c24; padding: 10px; margin-bottom: 15px; border: 1px solid #f5c6cb; border-radius: 4px;"><strong>TEST MODE</strong><br>Original Recipient: ${originalRecipient}<br>This is a test email that would normally be sent to the contact.</div>${emailData.body}`
        };
        
        console.log('Redirecting email to test address:', {
          originalRecipient,
          testRecipient: emailToSend.to
        });
      } else {
        // console.log('LIVE MODE - Sending email to actual recipient:', emailData.to);
      }
      
      // Send the email (either the test version or the original)
      const result = await this.sendEmail(emailToSend, mailbox);
      return result;
    } catch (error) {
      console.error('Error composing email:', error);
      throw error;
    }
  }
}

const outlookService = new OutlookService();
export default outlookService; 